import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ChartBarIcon,
  DocumentTextIcon,
  TrophyIcon,
  CurrencyDollarIcon,
  BellAlertIcon,
} from '@heroicons/react/24/outline';
import { reportsAPI, batchesAPI, membersAPI } from '../services/api';
import {
  LoadingSpinner,
  StatCard,
  Badge,
  DataTable,
} from '../components/common';
import { formatCurrency, formatDate, formatNumber } from '../utils/formatters';

export default function ReportsPage() {
  const [reportType, setReportType] = useState('summary');
  const [selectedBatchId, setSelectedBatchId] = useState('');
  const [selectedMemberId, setSelectedMemberId] = useState('');

  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['report-summary'],
    queryFn: () => reportsAPI.getSummary(),
    enabled: reportType === 'summary',
  });

  const { data: batchReport } = useQuery({
    queryKey: ['batch-report', selectedBatchId],
    queryFn: () => reportsAPI.getBatchReport(selectedBatchId),
    enabled: reportType === 'batch' && !!selectedBatchId,
  });

  const { data: memberReport } = useQuery({
    queryKey: ['member-report', selectedMemberId],
    queryFn: () => reportsAPI.getMemberReport(selectedMemberId),
    enabled: reportType === 'member' && !!selectedMemberId,
  });

  const { data: winnersReport } = useQuery({
    queryKey: ['winners-report'],
    queryFn: () => reportsAPI.getWinnersReport(),
    enabled: reportType === 'winners',
  });

  const { data: feesReport } = useQuery({
    queryKey: ['fees-report'],
    queryFn: () => reportsAPI.getFeesReport(),
    enabled: reportType === 'fees',
  });

  const { data: remindersReport } = useQuery({
    queryKey: ['reminders-report'],
    queryFn: () => reportsAPI.getRemindersReport(),
    enabled: reportType === 'reminders',
  });

  const { data: batches } = useQuery({
    queryKey: ['batches-list'],
    queryFn: async () => {
      const response = await batchesAPI.getAll();
      return Array.isArray(response.data) ? response.data : (response.data?.data || []);
    },
    enabled: reportType === 'batch',
  });

  const isLoading = summaryLoading;

  const reportTypes = [
    { id: 'summary', label: 'System Summary', icon: ChartBarIcon },
    { id: 'batch', label: 'Batch Report', icon: DocumentTextIcon },
    { id: 'member', label: 'Member Report', icon: DocumentTextIcon },
    { id: 'winners', label: 'Winners Report', icon: TrophyIcon },
    { id: 'fees', label: 'Fees Report', icon: CurrencyDollarIcon },
    { id: 'reminders', label: 'Reminders Report', icon: BellAlertIcon },
  ];

  if (isLoading) {
    return <LoadingSpinner text="Loading reports..." />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Reports</h1>
        <p className="mt-2 text-sm text-gray-600">
          Generate and view system reports
        </p>
      </div>

      {/* Report Type Selector */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Select Report Type</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {reportTypes.map((type) => {
            const Icon = type.icon;
            return (
              <button
                key={type.id}
                onClick={() => setReportType(type.id)}
                className={`p-4 rounded-lg border-2 transition-all ${
                  reportType === type.id
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Icon className={`h-6 w-6 mx-auto mb-2 ${
                  reportType === type.id ? 'text-primary-600' : 'text-gray-600'
                }`} />
                <p className={`text-sm font-medium ${
                  reportType === type.id ? 'text-primary-900' : 'text-gray-700'
                }`}>
                  {type.label}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      {/* System Summary Report */}
      {reportType === 'summary' && summary?.data && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              title="Total Batches"
              value={summary.data.total_batches || 0}
              icon={ChartBarIcon}
              color="primary"
            />
            <StatCard
              title="Total Members"
              value={summary.data.total_members || 0}
              icon={ChartBarIcon}
              color="blue"
            />
            <StatCard
              title="Total Revenue"
              value={formatCurrency(summary.data.total_revenue_cents || 0)}
              icon={CurrencyDollarIcon}
              color="green"
            />
            <StatCard
              title="Total Winners"
              value={summary.data.total_winners || 0}
              icon={TrophyIcon}
              color="yellow"
            />
          </div>

          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Status Breakdown</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <p className="text-3xl font-bold text-green-600">{summary.data.payment_status_green || 0}</p>
                <p className="text-sm text-gray-600 mt-1">Green</p>
              </div>
              <div className="text-center p-4 bg-yellow-50 rounded-lg">
                <p className="text-3xl font-bold text-yellow-600">{summary.data.payment_status_yellow || 0}</p>
                <p className="text-sm text-gray-600 mt-1">Yellow</p>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <p className="text-3xl font-bold text-red-600">{summary.data.payment_status_red || 0}</p>
                <p className="text-sm text-gray-600 mt-1">Red</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-3xl font-bold text-gray-600">{summary.data.payment_status_defaulted || 0}</p>
                <p className="text-sm text-gray-600 mt-1">Defaulted</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Batch Report */}
      {reportType === 'batch' && (
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Batch Report</h3>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Batch
            </label>
            <select
              value={selectedBatchId}
              onChange={(e) => setSelectedBatchId(e.target.value)}
              className="input-field max-w-md"
            >
              <option value="">Select a batch...</option>
              {batches?.map((batch) => (
                <option key={batch.id} value={batch.id}>
                  {batch.name || `Batch ${batch.batch_number}`}
                </option>
              ))}
            </select>
          </div>

          {batchReport?.data && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Total Members</p>
                  <p className="text-2xl font-bold text-gray-900">{batchReport.data.total_members || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Deposits</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(batchReport.data.total_deposits_cents || 0)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Draws</p>
                  <p className="text-2xl font-bold text-gray-900">{batchReport.data.total_draws || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Winners</p>
                  <p className="text-2xl font-bold text-gray-900">{batchReport.data.total_winners || 0}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Member Report */}
      {reportType === 'member' && (
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Member Report</h3>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Enter Member ID
            </label>
            <input
              type="number"
              value={selectedMemberId}
              onChange={(e) => setSelectedMemberId(e.target.value)}
              className="input-field max-w-md"
              placeholder="Enter member ID..."
            />
          </div>

          {memberReport?.data && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Total Deposits</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(memberReport.data.total_deposits_cents || 0)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Batches Enrolled</p>
                  <p className="text-2xl font-bold text-gray-900">{memberReport.data.batches_enrolled || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Wins</p>
                  <p className="text-2xl font-bold text-gray-900">{memberReport.data.total_wins || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Days Covered</p>
                  <p className="text-2xl font-bold text-gray-900">{memberReport.data.days_covered || 0}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Winners Report */}
      {reportType === 'winners' && winnersReport?.data && (
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Winners Report</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="text-center p-4 bg-primary-50 rounded-lg">
              <p className="text-3xl font-bold text-primary-600">{winnersReport.data.total_winners || 0}</p>
              <p className="text-sm text-gray-600 mt-1">Total Winners</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-3xl font-bold text-green-600">
                {formatCurrency(winnersReport.data.total_prize_amount_cents || 0)}
              </p>
              <p className="text-sm text-gray-600 mt-1">Total Prize Amount</p>
            </div>
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <p className="text-3xl font-bold text-yellow-600">{winnersReport.data.pending_payouts || 0}</p>
              <p className="text-sm text-gray-600 mt-1">Pending Payouts</p>
            </div>
          </div>
        </div>
      )}

      {/* Fees Report */}
      {reportType === 'fees' && feesReport?.data && (
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Fees Report</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-3xl font-bold text-blue-600">
                {formatCurrency(feesReport.data.total_service_fees_cents || 0)}
              </p>
              <p className="text-sm text-gray-600 mt-1">Total Service Fees</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-3xl font-bold text-green-600">
                {formatCurrency(feesReport.data.total_draw_fees_cents || 0)}
              </p>
              <p className="text-sm text-gray-600 mt-1">Total Draw Fees</p>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <p className="text-3xl font-bold text-purple-600">
                {formatCurrency(feesReport.data.total_fees_cents || 0)}
              </p>
              <p className="text-sm text-gray-600 mt-1">Total Fees Collected</p>
            </div>
          </div>
        </div>
      )}

      {/* Reminders Report */}
      {reportType === 'reminders' && remindersReport?.data && (
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Reminders Report</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-primary-50 rounded-lg">
              <p className="text-3xl font-bold text-primary-600">{remindersReport.data.total_reminders || 0}</p>
              <p className="text-sm text-gray-600 mt-1">Total Reminders</p>
            </div>
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <p className="text-3xl font-bold text-yellow-600">{remindersReport.data.available_reminders || 0}</p>
              <p className="text-sm text-gray-600 mt-1">Available</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-3xl font-bold text-green-600">{remindersReport.data.used_reminders || 0}</p>
              <p className="text-sm text-gray-600 mt-1">Used</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
